/**
 * VersichererStammdaten — the Stammdaten/CRM card on the Versicherer-Übersicht (P2 hardening 02).
 *
 * Modelled 1:1 on FirmenStammdaten: a read card grouped «Register» (Name im Register, Rechtsform,
 * UID, Adresse, PLZ/Ort, Kanton, NOGA/Branche, Gründungsjahr — each with an "Auto" badge unless the
 * key is in manual_overrides) + «Kontakt» (Telefon, Website, E-Mail allgemein, E-Mail
 * Schadenmeldung, Notizen); the enrichment banner; a pencil edit (crm.write → PATCH /carriers/:uid);
 * and a RefreshCw button (POST /carriers/:uid/enrich) that polls GET /carriers/:uid until the
 * zefix_status settles, with a cooldown + 429/422 surfaced via ApiError.
 *
 * Live mode only (the carrier id is the register UID there); in mock mode a minimal read card is
 * rendered from the list-derived Carrier.
 *
 * [Source: apps/web/src/features/entity-detail/components/FirmenStammdaten.tsx] (the card this mirrors)
 */

import { useState } from 'react';
import { AlertCircle, CheckCircle2, Info, Pencil, RefreshCw, Sparkles, X } from 'lucide-react';
import { apiClient, ApiError } from '../../../lib/api-client';
import { Button } from '../../administration/components';
import { useCarrierDetail } from '../../carriers/hooks/useCarrierRows';
import type { Carrier } from '../types';

const LEGAL_FORM_LABEL: Record<string, string> = {
  ag: 'Aktiengesellschaft (AG)', gmbh: 'GmbH', eg: 'Genossenschaft',
  einzelfirma: 'Einzelfirma', verein: 'Verein', stiftung: 'Stiftung', other: 'Andere',
};

function enrichmentLabel(status: string | undefined): string {
  switch (status) {
    case 'done': return 'Aus dem Handelsregister übernommen';
    case 'pending': return 'Noch nicht abgeglichen';
    case 'in_progress': return 'Abgleich läuft';
    case 'failed': return 'Abgleich fehlgeschlagen';
    default: return 'Unbekannt';
  }
}

const ENRICH_POLL_INTERVAL_MS = 1500;
const ENRICH_POLL_MAX = 10;
const ENRICH_CLIENT_COOLDOWN_MS = 30_000;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function AutoBadge() {
  return (
    <span className="inline-flex shrink-0 items-center gap-0.5 rounded bg-muted px-1 py-0.5 text-[10px] font-medium text-muted-foreground" title="Automatisch aus dem Handelsregister, überschreibbar.">
      <Sparkles className="size-2.5" aria-hidden /> Auto
    </span>
  );
}

function Row({ label, value, auto = false }: { label: string; value: string | null | undefined; auto?: boolean }) {
  return (
    <div className="flex items-start gap-3 py-1.5 text-sm">
      <div className="flex w-36 shrink-0 flex-col gap-1 text-xs text-muted-foreground">
        <span>{label}</span>
        {auto && <AutoBadge />}
      </div>
      <span className="min-w-0 flex-1 break-words">{value?.trim() ? value : '-'}</span>
    </div>
  );
}

// Keys the register fills — "Auto" unless the broker has overridden them (manual_overrides).
const AUTO_KEYS = new Set(['display_name', 'legal_form', 'address', 'postcode', 'city', 'canton', 'noga_code', 'branche', 'founding_year']);

export function VersichererStammdaten({
  uid,
  canManage,
  fallback,
}: {
  uid: string;
  canManage: boolean;
  fallback: Carrier;
}) {
  const { data, isLoading, refetch } = useCarrierDetail(uid);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [enrichRunning, setEnrichRunning] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error' | 'info'; text: string } | null>(null);

  const cooldownRemaining = cooldownUntil !== null ? Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000)) : 0;

  // Mock mode (no live detail) → a minimal read card from the list row.
  if (!data) {
    return (
      <section className="rounded-lg border bg-card" aria-label="Versicherer-Stammdaten">
        <header className="border-b px-4 py-3"><h3 className="text-sm font-semibold">Stammdaten</h3></header>
        <div className="px-4 py-2">
          {isLoading ? (
            <p className="py-2 text-sm text-muted-foreground">Wird geladen …</p>
          ) : (
            <div className="flex flex-col divide-y">
              <Row label="Name" value={fallback.name} />
              <Row label="Ort" value={fallback.city} />
              <Row label="Kanton" value={fallback.canton} />
            </div>
          )}
        </div>
      </section>
    );
  }

  const overrides = new Set(data.manual_overrides ?? []);
  const isAuto = (key: string) => AUTO_KEYS.has(key) && !overrides.has(key);
  const ort = [data.postcode, data.city].filter((v) => v?.trim()).join(' ') || null;

  function startEdit() {
    setDraft({
      display_name: data!.display_name ?? '',
      phone: data!.phone ?? '',
      website: data!.website ?? '',
      email_general: data!.email_general ?? '',
      email_schaden: data!.email_schaden ?? '',
      notes: data!.notes ?? '',
    });
    setSaveError(null);
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    try {
      await apiClient.carriers.update(uid, {
        display_name: draft.display_name!.trim() === '' ? undefined : draft.display_name!.trim(),
        phone: draft.phone!.trim() === '' ? null : draft.phone!.trim(),
        website: draft.website!.trim() === '' ? null : draft.website!.trim(),
        email_general: draft.email_general!.trim() === '' ? null : draft.email_general!.trim(),
        email_schaden: draft.email_schaden!.trim() === '' ? null : draft.email_schaden!.trim(),
        notes: draft.notes!.trim() === '' ? null : draft.notes!.trim(),
      });
      await refetch();
      setEditing(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  async function handleEnrich() {
    if (enrichRunning || cooldownRemaining > 0) return;
    setMsg(null);
    setEnrichRunning(true);
    try {
      await apiClient.carriers.enrich(uid);
      setCooldownUntil(Date.now() + ENRICH_CLIENT_COOLDOWN_MS);
      let settled: string | null = null;
      for (let i = 0; i < ENRICH_POLL_MAX; i++) {
        await sleep(ENRICH_POLL_INTERVAL_MS);
        const res = await refetch();
        const status = res.data?.enrichment.zefix_status;
        if (status === 'done' || status === 'failed') { settled = status; break; }
      }
      setMsg(
        settled === 'done'
          ? { kind: 'success', text: 'Stammdaten aus dem Handelsregister aktualisiert.' }
          : settled === 'failed'
            ? { kind: 'error', text: 'Kein Registereintrag gefunden / Abgleich fehlgeschlagen.' }
            : { kind: 'info', text: 'Abgleich läuft noch, bitte in Kürze erneut prüfen.' },
      );
    } catch (err) {
      // Branch on err.code, NOT HTTP status: the server sends invalid_uid=400 and
      // enrich_in_progress=422 (every backfilled row is `pending` → the limiter blocks it as
      // in_progress, which is the COMMON state), so a status===422 → "ungültige UID" mapping
      // mislabels the normal "already running" case.
      if (err instanceof ApiError && err.code === 'invalid_uid') {
        setMsg({ kind: 'error', text: 'Abgleich nicht möglich (ungültige UID).' });
      } else if (err instanceof ApiError && err.code === 'enrich_in_progress') {
        setCooldownUntil(Date.now() + (err.retryAfterSeconds ?? 30) * 1000);
        setMsg({ kind: 'info', text: 'Abgleich läuft bereits.' });
      } else if (err instanceof ApiError && err.status === 429) {
        setCooldownUntil(Date.now() + (err.retryAfterSeconds ?? 30) * 1000);
        setMsg({ kind: 'info', text: 'Zu viele Anfragen, bitte kurz warten.' });
      } else {
        setMsg({ kind: 'error', text: err instanceof Error ? err.message : 'Abgleich fehlgeschlagen.' });
      }
    } finally {
      setEnrichRunning(false);
    }
  }

  const field = (key: string, label: string, type = 'text') => (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <input
        type={type}
        value={draft[key] ?? ''}
        disabled={saving}
        onChange={(e) => setDraft((p) => ({ ...p, [key]: e.target.value }))}
        aria-label={label}
        className="h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    </label>
  );

  return (
    <section className="rounded-lg border bg-card" aria-label="Versicherer-Stammdaten">
      <header className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <h3 className="text-sm font-semibold">Stammdaten</h3>
        <div className="flex items-center gap-2">
          {!editing && (
            <button
              type="button"
              onClick={() => void handleEnrich()}
              disabled={enrichRunning || cooldownRemaining > 0}
              aria-label="Stammdaten neu abgleichen"
              title={cooldownRemaining > 0 ? `Erneut möglich in ${cooldownRemaining}s` : 'Mit dem Handelsregister abgleichen'}
              className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-secondary disabled:opacity-50"
            >
              <RefreshCw className={`size-3.5 ${enrichRunning ? 'animate-spin' : ''}`} />
            </button>
          )}
          {!editing && <span className="text-xs text-muted-foreground">{enrichmentLabel(data.enrichment.zefix_status)}</span>}
          {!editing && canManage && (
            <button type="button" onClick={startEdit} aria-label="Stammdaten bearbeiten" className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-secondary">
              <Pencil className="size-3.5" />
            </button>
          )}
          {editing && (
            <button type="button" onClick={() => setEditing(false)} disabled={saving} aria-label="Bearbeiten abbrechen" className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-secondary disabled:opacity-50">
              <X className="size-3.5" />
            </button>
          )}
        </div>
      </header>

      <div className="px-4 py-2">
        {msg !== null && (
          <p className={`flex items-start gap-2 py-2 text-sm ${msg.kind === 'error' ? 'text-destructive' : msg.kind === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
            {msg.kind === 'error' ? <AlertCircle className="size-4 shrink-0" /> : msg.kind === 'success' ? <CheckCircle2 className="size-4 shrink-0" /> : <Info className="size-4 shrink-0" />}
            {msg.text}
          </p>
        )}

        {!editing ? (
          <div className="flex flex-col gap-4">
            <div>
              <p className="mb-1 text-xs font-semibold text-muted-foreground">Register</p>
              <div className="flex flex-col divide-y">
                <Row label="Name im Register" value={data.register?.name ?? data.display_name} auto={isAuto('display_name')} />
                <Row label="Rechtsform" value={data.legal_form ? (LEGAL_FORM_LABEL[data.legal_form] ?? data.legal_form) : null} auto={isAuto('legal_form')} />
                <Row label="UID" value={data.carrier_uid} />
                <Row label="Adresse" value={data.address} auto={isAuto('address')} />
                <Row label="PLZ / Ort" value={ort} auto={isAuto('city')} />
                <Row label="Kanton" value={data.canton} auto={isAuto('canton')} />
                <Row label="NOGA / Branche" value={[data.noga_code, data.branche].filter(Boolean).join(' · ') || null} auto={isAuto('noga_code')} />
                <Row label="Gründungsjahr" value={data.founding_year != null ? String(data.founding_year) : null} auto={isAuto('founding_year')} />
              </div>
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-muted-foreground">Kontakt</p>
              <div className="flex flex-col divide-y">
                <Row label="Kurzname" value={data.display_name} />
                <Row label="Telefon" value={data.phone} />
                <Row label="Website" value={data.website} />
                <Row label="E-Mail allgemein" value={data.email_general} />
                <Row label="E-Mail Schadenmeldung" value={data.email_schaden} />
                <Row label="Notizen" value={data.notes} />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 py-2">
            {field('display_name', 'Kurzname')}
            {field('phone', 'Telefon')}
            {field('website', 'Website', 'url')}
            {field('email_general', 'E-Mail allgemein', 'email')}
            {field('email_schaden', 'E-Mail Schadenmeldung', 'email')}
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Notizen</span>
              <textarea
                value={draft.notes ?? ''}
                disabled={saving}
                onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))}
                aria-label="Notizen"
                className="min-h-16 rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </label>
            {saveError !== null && <p className="text-sm text-destructive">{saveError}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={saving}>Abbrechen</Button>
              <Button size="sm" onClick={() => void save()} disabled={saving}>{saving ? 'Speichern…' : 'Speichern'}</Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
